const LANGUAGE_DEFINITIONS = [
  ['en-GB', 'English'],
  ['cy-GB', 'Cymraeg'],
  ['fr-FR', 'Français'],
  ['de-DE', 'Deutsch'],
  ['ro-RO', 'Română'],
  ['ka-GE', 'ქართული'],
  ['nl-NL', 'Nederlands'],
  ['it-IT', 'Italiano'],
  ['el-GR', 'Ελληνικά'],
  ['es-ES', 'Español'],
  ['pt-PT', 'Português'],
  ['fi-FI', 'Suomi'],
  ['nb-NO', 'Norsk bokmål'],
  ['sv-SE', 'Svenska'],
  ['da-DK', 'Dansk'],
  ['pl-PL', 'Polski'],
  ['lv-LV', 'Latviešu'],
  ['lt-LT', 'Lietuvių'],
  ['cs-CZ', 'Čeština'],
  ['sk-SK', 'Slovenčina'],
  ['et-EE', 'Eesti'],
  ['gu-IN', 'ગુજરાતી'],
  ['bn-BD', 'বাংলা'],
  ['pa-IN', 'ਪੰਜਾਬੀ'],
  ['ur-PK', 'اردو'],
  ['ar-AE', 'العربية (الإمارات)'],
] as const;

export type AppLanguageCode = (typeof LANGUAGE_DEFINITIONS)[number][0];
export type TextDirection = 'ltr' | 'rtl';

export const APP_LANGUAGES = LANGUAGE_DEFINITIONS.map(([code, nativeName]) => ({
  code,
  shortCode: code.split('-')[0],
  translationKey: code.replace('-', '_'),
  nativeName,
  direction: (code === 'ar-AE' || code === 'ur-PK'
    ? 'rtl'
    : 'ltr') as TextDirection,
}));

export const getAppLanguage = (value: string) => {
  const normalized = value.trim().replace(/_/g, '-').toLowerCase();
  // Keep the historical external Welsh link working.
  const code = normalized === 'cy-cy' ? 'cy-gb' : normalized;
  return (
    APP_LANGUAGES.find((language) => language.code.toLowerCase() === code) ??
    APP_LANGUAGES.find((language) => language.shortCode === code)
  );
};

export const getLanguageDirection = (value: string): TextDirection =>
  getAppLanguage(value)?.direction ??
  (/^(ar|ur)(-|_|$)/i.test(value) ? 'rtl' : 'ltr');

export const LANGUAGE_NAME_OVERRIDES = Object.fromEntries(
  APP_LANGUAGES.filter(({ shortCode }) =>
    ['gu', 'bn', 'pa', 'ur', 'ar'].includes(shortCode)
  ).map(({ translationKey, nativeName }) => [translationKey, nativeName])
);

export const getFullLocaleCode = (value: string): string => {
  const code = getAppLanguage(value)?.code ?? 'en-GB';
  return code === 'cy-GB' ? 'cy_cy' : code.replace('-', '_').toLowerCase();
};

export const getSpeechLocale = (value: string): string | undefined =>
  getAppLanguage(value)?.code;
